/* eslint-disable react/prop-types */
import React from 'react'
import { scaleLinear } from 'd3-scale'
import { max } from 'd3-array'
import groupBy from 'lodash/groupBy'
import { arc, pie, PieArcDatum } from 'd3-shape'
import Color from '../../Utilities/Colors/ColorMap.ts'
import PieLegend from '../../Legend/PieLegend.tsx'
import Legend from '../../Legend/Legend.tsx'
import { EVPData } from '../../schema/types.ts'
import {
    useChartOptions,
    useScales,
    useStrikeZonePositions,
} from '../../Context/ChartContext.tsx'

export default function PitchPie() {
    const { chartOptions, settings } = useChartOptions()

    const { xScale, yScale } = useScales()

    const zonePositions = useStrikeZonePositions()

    const pitchData = groupPitchesAndLocation(settings.data, zonePositions)

    const pitches = groupBy(pitchData, 'pzpx')

    let radius = 15

    let radiusScale
    if (chartOptions.scaleRadius) {
        const maxes = []
        // eslint-disable-next-line no-restricted-syntax, guard-for-in
        for (const zone in pitches) {
            let count = 0
            for (let i = 0; i < pitches[zone].length; i++) {
                count += Number(pitches[zone][i].count)
            }
            maxes.push(count)
        }
        const maxValue = max(maxes, (d) => Number(d)) || 100

        radiusScale = scaleLinear()
            .range([settings.vizWidth * 0.025, settings.vizWidth * 0.045])
            .domain([1, maxValue])
    }

    const pitchPie = []

    // eslint-disable-next-line no-restricted-syntax, guard-for-in
    for (const pitch in pitches) {
        let size = 0
        for (let i = 0; i < pitches[pitch].length; i++) {
            size += Number(pitches[pitch][i].count)
        }

        if (chartOptions.scaleRadius && radiusScale) {
            radius = radiusScale(size)
        }

        const pp = pie<{
            count: number
            px: string
            pz: string
            pitch: string
        }>()
            .sort(null)
            .value((d) => Number(d.count))

        const path = arc<
            unknown,
            PieArcDatum<{
                count: number
                px: string
                pz: string
                pitch: string
            }>
        >()
            .outerRadius(radius - settings.vizWidth * 0.014)
            .innerRadius(0)

        pitchPie.push(
            pp(pitches[pitch]).map((d, i) => (
                <g
                    // eslint-disable-next-line react/no-array-index-key
                    key={`pie_key_${pitch}_${d.data.count}_${i}`}
                    id={`id_${pitch}_${d.data.count}`}
                >
                    <path
                        transform={`translate(${xScale(
                            Number(d.data.px)
                        )},${yScale(Number(d.data.pz))})`}
                        d={path(d) ?? undefined}
                        fill={Color('pitch_name', d.data.pitch)}
                        stroke="#000"
                        strokeWidth={0.5}
                    />
                </g>
            ))
        )
    }
    return (
        <>
            <g id="pitchPie">{pitchPie}</g>
            {chartOptions.legend ? (
                <>
                    <Legend settings={settings} type="pitchName" />
                    {radiusScale ? (
                        <PieLegend
                            settings={settings}
                            radiusScale={radiusScale}
                        />
                    ) : null}
                </>
            ) : null}
        </>
    )
}

const BIN_SIZE = 0.5
function roundAndGroupPitches(
    pitches: EVPData[],
    normalizedPositions: { x: number; z: number }[]
): (EVPData & { piePx: string; piePz: string; pieId: string })[] {
    return pitches.map((p, i) => {
        const { x, z } = normalizedPositions[i]
        const piePx = (Math.round(Number(x) / BIN_SIZE) * BIN_SIZE).toFixed(2)
        const piePz = (Math.round(Number(z) / BIN_SIZE) * BIN_SIZE).toFixed(2)
        // new
        const pieId = `p_${piePz}${piePx}`
            .replaceAll('-', '_')
            .replaceAll('.', '')
        // old
        // const pieId1 = `p_${piePz < 0 ? `_${Math.abs(piePz)}` : piePz}${
        //     piePx < 0 ? `_${Math.abs(piePx)}` : piePx
        // }`
        // const pieIdOld = pieId1.split('.').join('')

        return {
            ...p,
            piePx,
            piePz,
            pieId,
        }
    })
}

function groupPitchesAndLocation(
    pitches: EVPData[],
    normalizedPositions: { x: number; z: number }[]
) {
    //  const pitches = removeBadPitches
    const roundedPitches = roundAndGroupPitches(pitches, normalizedPositions)

    const pitchLocations = groupBy(roundedPitches, (d) => d.pieId)

    const pitchBreakdown = []
    // eslint-disable-next-line no-restricted-syntax, guard-for-in
    for (const location in pitchLocations) {
        const l = pitchLocations[location]

        const pitchTypes = groupBy(l, (d) => d.pitchType)

        // eslint-disable-next-line no-restricted-syntax, guard-for-in
        for (const pitchType in pitchTypes) {
            const p = pitchTypes[pitchType]
            pitchBreakdown.push({
                count: p.length,
                px: p[0].piePx,
                pz: p[0].piePz,
                pzpx: p[0].pieId,
                pitch: pitchType,
            })
        }
    }

    return pitchBreakdown
}
